import { Alert } from '@kula-ai/components';
import { FormBuilder } from '@/components/formBuilder';
import { MAILBOX_NOT_CONNECTED_MESSAGE } from '@/components/shared/interview-manager/ScheduleInterview/config/senderErrorCopy';
import { SurveyEmailComposer } from './SurveyEmailComposer';
import {
  canSendAs,
  getSurveyEmailInitialValue,
} from './surveyEmailInitialValue';
import { templatesCategoryConfig } from '@/constants/templates-category';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { getInvalidVariableAlertConfig } from '@/formConfigs/getInvalidVariableAlertConfig';
import { Box } from '@chakra-ui/react';
import { useSurveyTemplates } from './useSurveyTemplates';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { ScheduleForLater } from '@/components/ats/job-stage-actions/actions/assessment/send-assessment/schedule/ScheduleForLater';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { CenterSpinner } from '@/components/ats';
import { toVariablesList } from '@/utils/emailComposition';

interface SurveyFormEditorProps {
  formRef?: any;
  selectedTemplate?: any;
  handleFormSubmit?: () => void;
  selectOnChange?: () => void;
  activity?: any;
  surveyType?: string;
  isScheduled?: boolean;
  isInterviewPlan?: boolean;
  scheduling?: {
    scheduleRef: any;
    handleSchedule: (_scheduleValues: any) => void;
    handleCancelSchedule: () => void;
    isScheduling: boolean;
    isCanceling: boolean;
  };
}

export const SurveyFormEditor: React.FC<SurveyFormEditorProps> = ({
  formRef = null,
  selectedTemplate: selectedTemplateProp = {},
  handleFormSubmit = () => {},
  selectOnChange = () => {},
  activity = {},
  surveyType = '',
  isScheduled = false,
  isInterviewPlan = false,
  scheduling = {
    scheduleRef: null,
    handleSchedule: () => {},
    handleCancelSchedule: () => {},
    isScheduling: false,
    isCanceling: false,
  },
}) => {
  // Derive effective template: use prop if available, otherwise fallback to activity data
  // Note: Activity object uses candidate_comm_template (mapped from API's ats_comm_template)
  const selectedTemplate = useMemo(
    () =>
      selectedTemplateProp?.id
        ? selectedTemplateProp
        : activity?.candidate_comm_template || {},
    [selectedTemplateProp, activity?.candidate_comm_template]
  );
  const view = useMemo(() => {
    const baseView = templatesCategoryConfig.survey_notice.view;
    if (surveyType === 'employee') {
      return baseView;
    }
    return baseView.filter((item) => item !== 'user');
  }, [surveyType]);

  const { data: { variables: rawVariables = {} } = {} } =
    useGetTemplatesVariablesQuery({
      params: { view },
    });

  // Filter out user.email_signature from variables for survey context
  const variables = useMemo(() => {
    const filtered = { ...rawVariables };
    if (filtered.user && Array.isArray(filtered.user)) {
      filtered.user = filtered.user.filter(
        (variable: string) => variable !== 'user.email_signature'
      );
    }
    return filtered;
  }, [rawVariables]);

  const variablesListArr = toVariablesList(variables);

  const users = useSelector(selectUsers);
  const currUser = useSelector(selectCurrUser);
  const { fromUsers, isFromUsersLoading } = useGetAllUsers();
  // include ToAddress field when survey type is employee
  const toAddress = useMemo(() => {
    if (surveyType === 'employee') {
      return users.map((user) => ({
        email: user.email,
        name: user.name,
        id: user.id,
        avatar: user.avatar,
      }));
    }
    return [];
  }, [surveyType, users]);

  // In edit mode (activity.id exists), don't auto-select initial template
  const isEditMode = !!activity?.id;

  // Why From came up blank. Only for the seed we withheld — a survey with no
  // sender of its own, where From would otherwise have defaulted to the signed-in
  // user. Without this the send-survey drawer, where From is mandatory, offers a
  // required field the user cannot fill and says only "From is mandatory".
  const showMailboxNotConnected =
    !isFromUsersLoading &&
    !activity?.candidate_comm_sender?.id &&
    !activity?.ats_shared_email?.id &&
    !canSendAs(currUser, fromUsers);

  const { templates, loadTemplates } = useSurveyTemplates({
    surveyType,
    selectedTemplate,
    isEditMode,
    onSelect: selectOnChange,
  });

  const composerProps = useMemo(
    () => ({
      fromUsers,
      ccBccUsers: users,
      toUsers: toAddress,
      variables,
      // From/To are mandatory when sending a survey, not in the plan editor.
      isRequired: !isInterviewPlan,
      // The picker lives inside the email card — no separate select above it.
      templates,
      selectedTemplate,
      onTemplateChange: selectOnChange,
      loadTemplates,
    }),
    [
      fromUsers,
      users,
      toAddress,
      variables,
      isInterviewPlan,
      templates,
      selectedTemplate,
      selectOnChange,
      loadTemplates,
    ]
  );

  const surveyEmailFormConfig = [
    {
      id: 'emailFormConfig',
      type: 'wrapper',
      fields: [
        {
          id: 'surveyEmailComposer',
          type: 'custom',
          Field: SurveyEmailComposer,
          props: composerProps,
        },
      ],
      Wrapper: Box,
      // No border here — the kula card draws its own, so both would stack.
      wrapperProps: {
        marginTop: '12px',
      },
    },
    getInvalidVariableAlertConfig({
      variablesListArr,
      isBodySubject: true,
    }),
  ];

  // Wait for the delegatable-senders list before computing the initial
  // From value — otherwise the validation below sees an empty list and
  // always falls back to the current user, even when the saved sender
  // is actually still delegatable.
  if (isFromUsersLoading) {
    return <CenterSpinner flex="1" />;
  }

  return (
    <Box px="24px" pt="16px">
      {showMailboxNotConnected && (
        <Alert
          status="warning"
          size="sm"
          title="You can't be the sender"
          description={MAILBOX_NOT_CONNECTED_MESSAGE}
        />
      )}
      <FormBuilder
        key={selectedTemplate?.id}
        initialValues={getSurveyEmailInitialValue(
          activity,
          selectedTemplate,
          variablesListArr,
          currUser,
          fromUsers
        )}
        config={surveyEmailFormConfig}
        onSubmit={handleFormSubmit}
        formRef={formRef}
      />
      {isScheduled && (
        <Box mb="80px">
          <ScheduleForLater
            scheduleRef={scheduling.scheduleRef}
            scheduleDetails={activity}
            onSchedule={(values) => scheduling.handleSchedule(values)}
            onCancelSchedule={scheduling.handleCancelSchedule}
            isScheduling={scheduling.isScheduling}
            isCanceling={scheduling.isCanceling}
            isScheduled={true}
            styles={{
              margin: '8px 0px !important',
            }}
          />
        </Box>
      )}
    </Box>
  );
};
